import { useMemo } from "react";

import {
  faUserCircle,
  faUserLargeSlash,
} from "@fortawesome/pro-regular-svg-icons";

import { useUserById, useWorkspaceUsers } from "src/api/taktile/queries";
import { useCaseQueue } from "src/cases/queries/queues";
import {
  OrganizationUser,
  WorkspaceSimpleRoleType,
} from "src/clients/taktile-api";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { useCurrentUserId, useIsFWDOrAdmin } from "src/store/AuthStore";
import { isWorkspaceAdmin } from "src/utils/user";

export const NO_ASSIGNEE_OPTION = {
  icon: faUserCircle,
  key: "null",
  value: "Unassigned",
} as const;

export const DELETED_ASSIGNEES_OPTION = {
  icon: faUserLargeSlash,
  key: "deleted_assignees",
  value: "Deleted Assignees",
} as const;

export const useQueueMembers = (queueId?: string, assigneeId?: string) => {
  const currentUserId = useCurrentUserId();
  const { workspace, orgId } = useWorkspaceContext();
  const isFWDOrAdmin = useIsFWDOrAdmin();
  const { data: queue, isLoading: isLoadingQueue } = useCaseQueue(queueId);
  const { data: users = [], isLoading: isLoadingUsers } = useWorkspaceUsers(
    orgId,
    workspace.id,
    {
      // We want to be able to display deactivated users
      include_deactivated: true,
      // We want only users with defined workspace roles
      workspace_role_filters: [
        WorkspaceSimpleRoleType.ADMIN,
        WorkspaceSimpleRoleType.EDITOR,
        WorkspaceSimpleRoleType.REVIEWER,
      ],
    },
  );

  const queueMemberIds = useMemo(
    () => new Set(queue?.members.map((m) => m.user.id) ?? []),
    [queue],
  );

  const { data: fetchedAssignee } = useUserById(assigneeId);

  const queueMembers = useMemo(() => {
    const members = users
      .filter((user): user is OrganizationUser => !!user)
      .filter(
        (user) =>
          (user.id === currentUserId &&
            (isWorkspaceAdmin(workspace.id, user) || isFWDOrAdmin)) ||
          queueMemberIds.has(user.id),
      );

    // Always include the assignee even if they're not a queue member
    if (assigneeId && !members.some((m) => m.id === assigneeId)) {
      const assigneeUser =
        users.find((u) => u.id === assigneeId) ??
        (fetchedAssignee
          ? ({ ...fetchedAssignee, organization_id: orgId } as OrganizationUser)
          : undefined);
      if (assigneeUser) {
        members.push(assigneeUser);
      }
    }

    return members;
  }, [
    users,
    queueMemberIds,
    workspace.id,
    currentUserId,
    isFWDOrAdmin,
    assigneeId,
    fetchedAssignee,
    orgId,
  ]);

  return {
    data: queueMembers,
    isLoading: isLoadingQueue || isLoadingUsers,
    queueMemberIds,
  };
};
